import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUsersStore } from 'src/store/users';
import { useAuthStore } from 'src/store/auth';
import { RoleType } from 'src/constants';

const AdminCreate = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();
  const { loading, create } = useUsersStore();

  // Check if user is admin, redirect to root if not
  useEffect(() => {
    if (currentUser && currentUser.role !== RoleType.Admin) {
      navigate('/');
    }
  }, [currentUser, navigate]);

  const [newGroupHash, setNewGroupHash] = useState<string>('admin');
  const [newRole, setNewRole] = useState<number>(RoleType.Admin);
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [newMidName, setNewMidName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [createError, setCreateError] = useState<string>('');
  const [showPasswordValidation, setShowPasswordValidation] = useState(false);

  const handleCreateSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCreateError('');
    const form = e.currentTarget;
    // Trigger native HTML5 validation highlighting
    if (!form.reportValidity()) {
      return;
    }
    // Show password validation after submit; block submit if invalid
    const passwordInvalid =
      newPassword.length < 8 ||
      !/[A-Z]/.test(newPassword) ||
      !/[a-z]/.test(newPassword) ||
      !/[0-9]/.test(newPassword) ||
      !/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/`~;']/.test(newPassword);
    if (passwordInvalid) {
      setShowPasswordValidation(true);
      return;
    }
    try {
      const groupHash = import.meta.env.VITE_APP_GROUP_HASH || document.querySelector('meta[name="x-group-hash"]')?.getAttribute('content');
      await create({ 
        groupHash: `${groupHash}`, 
        firstName: newFirstName, 
        lastName: newLastName,
        midName: newMidName,
        email: newEmail, 
        phone: newPhone,
        password: newPassword, 
        role: newRole, 
        status: 0 
      });
      navigate('/admins');
    } catch (err: any) {
      const apiErrors = err?.response?.data?.errors as { field: string; errors: string[] }[] | undefined;
      if (Array.isArray(apiErrors) && apiErrors.length) {
        setCreateError(apiErrors.map((e: any) => `${e.field}: ${e.errors?.join(', ') || ''}`).join(' | '));
      } else {
        setCreateError(err?.response?.data?.message || err?.message || 'Request failed');
      }
    }
  };

  const handleBackToList = () => {
    navigate('/admins');
  };

  return (
    <div className="flex-1 h-full rounded-[32px] p-2 id-card-container overflow-y-auto max-lg:my-2.5 relative">
      {/* Header */}
      <div className="px-8 py-6 flex items-center justify-between max-md:p-2">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBackToList}
            className="p-2 hover:bg-gray-100 flex items-center rounded-full"
          >
            <img src="/icons/left.svg" alt="back" />
          </button>
          <h1 className="id-card-title font-medium max-lg:!text-2xl max-md:!text-xl">
            Create New Admin
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-8 py-6 max-md:px-2">
        <div className="bg-white-overlay big-corner p-6 max-md:p-4">
          <form onSubmit={handleCreateSubmit} className="grid gap-4 max-w-3xl">
            <div className="grid grid-cols-2 gap-3">
              <select
                className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] border"
                value={newGroupHash}
                onChange={(e) => {
                  const v = e.target.value;
                  setNewGroupHash(v);
                  setNewRole(v === 'admin' ? RoleType.Admin : RoleType.Manager);
                }}
              >
                <option value="admin">Admin</option>
                <option value="manager">Manager</option>
              </select>
              <input 
                className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] border" 
                placeholder="First name" 
                value={newFirstName} 
                onChange={(e) => setNewFirstName(e.target.value)} 
                required 
              />
              <input 
                className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] border" 
                placeholder="Last name" 
                value={newLastName} 
                onChange={(e) => setNewLastName(e.target.value)} 
                required 
              />
              <input 
                className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] border" 
                placeholder="Middle name" 
                value={newMidName} 
                onChange={(e) => setNewMidName(e.target.value)} 
              />
              <input 
                className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] border" 
                placeholder="Email" 
                type="email" 
                value={newEmail} 
                onChange={(e) => setNewEmail(e.target.value)} 
                required 
              />
              <input 
                className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] border" 
                placeholder="Phone" 
                value={newPhone} 
                onChange={(e) => setNewPhone(e.target.value)} 
              />
              <input 
                className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] border" 
                placeholder="Password" 
                type="password" 
                value={newPassword} 
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setShowPasswordValidation(false);
                }} 
                required 
              />
            </div>
            
            {createError && (
              <p className="text-sm text-red-600">{createError}</p>
            )}
           {showPasswordValidation && (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/[0-9]/.test(newPassword) || !/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/`~;']/.test(newPassword)) && (
             <p className="text-sm text-red-600">Password must include uppercase and lowercase letters, numbers, and symbols.</p>
           )}
            
            <div className="flex gap-3 pt-4">
              {/* <button 
                type="button" 
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-full hover:bg-gray-300 transition-colors" 
                onClick={handleBackToList}
              >
                Cancel
              </button> */}
              <button 
                type="submit" 
                className="flex-1 px-4 py-2 bg-secondary-bg text-white rounded-full hover:bg-opacity-90 transition-colors" 
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminCreate;
